import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import { dateToMonDDYYYY } from '../utils/dateUtils';
import { getValidEnrollment } from '../utils/enrollmentUtils';

// Standard low blue color scheme
const CARD_COLOR = {
  bg: '#f0f8ff',
  border: '#87ceeb',
  text: '#4682b4',
  shadow: '#b0c4de'
};

const MyCourses = ({ userData }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [enrollmentId, setEnrollmentId] = useState(null);
  const [courses, setCourses] = useState([]);
  const navigate = useNavigate();

  const isCourseActive = (course) => {
    const today = new Date();
    const start = course.start_date ? new Date(String(course.start_date)) : null;
    const end = course.end_date ? new Date(String(course.end_date)) : null;
    const afterStart = !start || today >= new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const beforeEnd = !end || today <= new Date(end.getFullYear(), end.getMonth(), end.getDate());
    return afterStart && beforeEnd;
  };


  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const validEnrollment = getValidEnrollment(userData);
        const eid = validEnrollment?.enrollment_id || null;
        setEnrollmentId(eid);
        if (!eid) {
          setCourses([]);
          return;
        }
        const token = localStorage.getItem('token');
        const res = await axios.get('/api/course-enrollments', {
          params: { student: eid, limit: 200 },
          headers: { Authorization: `Bearer ${token}` }
        });
        const rows = res.data.courseEnrollments || [];
        // Filter to only show courses with 'enrolled' status
        const enrolledRows = rows.filter(r => r.status === 'enrolled');
        const mapped = enrolledRows.map(r => ({
          course_enrollment_id: r.id,
          course_enrollment_status: r.status,
          start_date: r.start_date,
          end_date: r.end_date,
          course_id: r.course,
          course_title: r.course_title,
          course_status: undefined
        }));
         // Sort by start_date - earliest first (12 o'clock position)
         mapped.sort((a, b) => {
           const da = a.start_date ? new Date(a.start_date) : null;
           const db = b.start_date ? new Date(b.start_date) : null;
           
           // If both have dates, sort by earliest first
           if (da && db) {
             return da - db;
           }
           
           // If only one has date, prioritize it
           if (da && !db) return -1;
           if (!da && db) return 1;
           
           // If neither has date, sort by title
           return a.course_title.localeCompare(b.course_title);
         });
        setCourses(mapped);
      } catch (err) {
        console.error('Error loading my courses:', err);
        setError('Failed to load courses');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userData]);

  if (loading) return <LoadingSpinner />;

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: '2rem 1rem' }}>
      <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
        <h1 style={{ color: '#1D4E89', fontSize: '2.5rem', margin: 0, fontWeight: '700' }}>My Courses</h1>
        <p style={{ color: '#666', fontSize: '1.1rem', marginTop: '0.5rem' }}>
          {courses.length > 0 ? `${courses.length} course${courses.length !== 1 ? 's' : ''} enrolled` : 'Your learning journey starts here'}
        </p>
      </div>

      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c6cb',
          borderRadius: 8,
          color: '#721c24',
          marginBottom: '2rem',
          textAlign: 'center'
        }}>{error}</div>
      )}

      {!enrollmentId ? (
        <div style={{ 
          textAlign: 'center', 
          color: '#666', 
          fontSize: '1.1rem',
          padding: '3rem 1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: 12,
          border: '1px solid #e0e0e0'
        }}>
          No active enrollment found.
        </div>
      ) : (
        <div style={{ marginTop: '2rem' }}>
          {courses.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              color: '#666', 
              fontSize: '1.1rem',
              padding: '3rem 1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: 12,
              border: '1px solid #e0e0e0'
            }}>
              You're not enrolled in any courses yet.
            </div>
          ) : (
             <div style={{
               display: 'grid',
               gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
               gap: '2rem',
               padding: '2rem 0'
             }}>
               {courses.map((course, index) => {
                 const active = isCourseActive(course);
                 const now = new Date();
                 const start = course.start_date ? new Date(String(course.start_date)) : null;
                 const end = course.end_date ? new Date(String(course.end_date)) : null;
                 const beforeStart = start && now < new Date(start.getFullYear(), start.getMonth(), start.getDate());
                 const afterEnd = end && now > new Date(end.getFullYear(), end.getMonth(), end.getDate());
                 
                 let statusLabel;
                 let statusStyle;
                 if (beforeStart) {
                   statusLabel = `Opens ${dateToMonDDYYYY(String(course.start_date), false)}`;
                   statusStyle = { color: '#856404', background: '#fff3cd', border: '1px solid #ffeeba' };
                 } else if (afterEnd) {
                   statusLabel = 'Closed';
                   statusStyle = { color: '#721c24', background: '#f8d7da', border: '1px solid #f5c6cb' };
                 } else {
                   statusLabel = course.end_date ? `Open • until ${dateToMonDDYYYY(String(course.end_date), false)}` : 'Open';
                   statusStyle = { color: '#155724', background: '#d4edda', border: '1px solid #c3e6cb' };
                 }

                 return (
                   <div
                     key={course.course_enrollment_id}
                     onClick={() => active && navigate(`/course-viewer?command=coursework&id=${course.course_id}`)}
                     style={{
                       background: `linear-gradient(135deg, ${CARD_COLOR.bg} 0%, ${CARD_COLOR.bg}dd 100%)`,
                       border: `2px solid ${CARD_COLOR.border}`,
                       borderRadius: 16,
                       padding: '2rem',
                       display: 'flex',
                       flexDirection: 'column',
                       justifyContent: 'space-between',
                       boxShadow: active ? `0 8px 25px ${CARD_COLOR.shadow}60` : `0 4px 15px ${CARD_COLOR.shadow}40`,
                       transition: 'all 0.3s ease',
                       cursor: active ? 'pointer' : 'not-allowed',
                       position: 'relative',
                       overflow: 'hidden',
                       minHeight: '300px'
                     }}
                     onMouseEnter={(e) => {
                       if (!active) return;
                       e.currentTarget.style.transform = 'translateY(-5px)';
                       e.currentTarget.style.boxShadow = `0 12px 35px ${CARD_COLOR.shadow}80`;
                     }}
                     onMouseLeave={(e) => {
                       e.currentTarget.style.transform = 'translateY(0)';
                       e.currentTarget.style.boxShadow = active ? `0 8px 25px ${CARD_COLOR.shadow}60` : `0 4px 15px ${CARD_COLOR.shadow}40`;
                     }}
                   >
                     {/* Simple decorative element */}
                     <div style={{
                       position: 'absolute',
                       top: '-20px',
                       right: '-20px',
                       width: '60px',
                       height: '60px',
                       background: `${CARD_COLOR.border}20`,
                       borderRadius: '50%',
                       opacity: 0.3
                     }} />

                     {/* Course content */}
                     <div>
                       <h3 style={{
                         color: CARD_COLOR.text,
                         fontSize: '1.3rem',
                         fontWeight: '700',
                         margin: '0 0 1rem 0',
                         lineHeight: '1.3',
                         zIndex: 1,
                         position: 'relative'
                       }}>
                         {course.course_title}
                       </h3>
                       
                       <div style={{
                         fontSize: '0.9rem',
                         color: CARD_COLOR.text,
                         opacity: 0.8,
                         marginBottom: '1rem',
                         zIndex: 1,
                         position: 'relative'
                       }}>
                         {course.start_date && (
                           <div>📅 {dateToMonDDYYYY(String(course.start_date), false)}</div>
                         )}
                         {course.end_date && (
                           <div>🏁 Ends: {dateToMonDDYYYY(String(course.end_date), false)}</div>
                         )}
                       </div>
                     </div>

                     {/* Status badge */}
                     <div style={{ zIndex: 1, position: 'relative' }}>
                       <span style={{
                         fontSize: '0.8rem',
                         fontWeight: '600',
                         ...statusStyle,
                         borderRadius: 16,
                         padding: '0.4rem 0.8rem',
                         display: 'inline-block',
                         boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                       }}>
                         {statusLabel}
                       </span>
                     </div>
                   </div>
                 );
               })}
             </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MyCourses;


